import { useState } from 'react';
import { Play } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { AGE_FILTER_OPTIONS, RATING_OPTIONS, WEBSITE_FILTER_OPTIONS } from '../constants';
import type { ScrapeParams } from '../types';

interface ScrapeConfigFormProps {
  disabled?: boolean;
  onStart: (params: ScrapeParams) => void;
}

export function ScrapeConfigForm({ disabled, onStart }: ScrapeConfigFormProps) {
  const [niche, setNiche] = useState('');
  const [location, setLocation] = useState('');
  const [minRating, setMinRating] = useState('0');
  const [maxResults, setMaxResults] = useState('20');
  const [minReviews, setMinReviews] = useState('0');
  const [websiteFilter, setWebsiteFilter] = useState('no-website');
  const [ageFilter, setAgeFilter] = useState('any');
  const [requirePhone, setRequirePhone] = useState(false);
  const [requireEmail, setRequireEmail] = useState(false);
  const [requireAddress, setRequireAddress] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!niche.trim() || !location.trim()) return;
    onStart({
      niche: niche.trim(),
      location: location.trim(),
      minRating: Number(minRating) || 0,
      maxResults: Math.min(Number(maxResults) || 20, 1000),
      minReviews: Number(minReviews) || 0,
      websiteFilter,
      ageFilter,
      requirePhone,
      requireEmail,
      requireAddress,
    });
  };

  const Checkbox = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) => (
    <label className="flex items-center gap-2 text-data-md text-on-surface">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-outline" />
      {label}
    </label>
  );

  return (
    <Card>
      <CardHeader title="Scrape Configuration" />
      <CardBody>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Niche *" name="scrape-niche" value={niche} onChange={(e) => setNiche(e.target.value)} placeholder="e.g. Cafes" required disabled={disabled} />
            <Input label="Location *" name="scrape-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Karachi" required disabled={disabled} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Select label="Minimum Rating" name="scrape-rating" value={minRating} onChange={(e) => setMinRating(e.target.value)} options={RATING_OPTIONS} disabled={disabled} />
            <Input label="Min Reviews" name="scrape-reviews" type="number" min={0} value={minReviews} onChange={(e) => setMinReviews(e.target.value)} disabled={disabled} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Select label="Website Filter" name="scrape-website" value={websiteFilter} onChange={(e) => setWebsiteFilter(e.target.value)} options={WEBSITE_FILTER_OPTIONS} disabled={disabled} />
            <Select label="Business Age" name="scrape-age" value={ageFilter} onChange={(e) => setAgeFilter(e.target.value)} options={AGE_FILTER_OPTIONS} disabled={disabled} />
          </div>

          <Input label="Result Limit (max 1000)" name="scrape-limit" type="number" min={1} max={1000} value={maxResults} onChange={(e) => setMaxResults(e.target.value)} disabled={disabled} />

          <div className="flex flex-wrap gap-4">
            <Checkbox label="Require phone" checked={requirePhone} onChange={setRequirePhone} />
            <Checkbox label="Require email" checked={requireEmail} onChange={setRequireEmail} />
            <Checkbox label="Require address" checked={requireAddress} onChange={setRequireAddress} />
          </div>

          <Button type="submit" disabled={disabled || !niche.trim() || !location.trim()} className="w-full">
            <Play className="h-4 w-4" /> Start Scan
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
